<template>
    <VBtn @click="showVehiclesModal = true" type="button" class="dropdown-btn inputs__dropdown" color="ghost">
        <span>{{ vehiclesPlaceholder }}</span>
    </VBtn>
    <VModal v-if="showVehiclesModal" @close="showVehiclesModal = false" title="Vehicles and bicycles">
        <div class="vehicle-select">
            <div class="heading">
                <p>Please note: Please be aware of any bike racks / roof racks that might affect the overall height of the vehicle.</p>
                <p>Commercial Vehicles must be booked via our Freight Department by calling 08001114434.</p>
            </div>
            <form @submit.prevent="submitVehicles" class="vehicle-selection">
                <div class="selections">
                    <div class="selections__vehicle">
                        <div v-if="differentDetails" class="title">Outward</div>
                        <div class="quantity">
                            <h4>Add vehicles and bicycles</h4>
                            <VQuantity class="quantity-input" id="vehicleSelect" v-model="numberOfVehiclesOutward" :minValue="0" />
                        </div>
                        <div v-for="index in numberOfVehiclesOutward" :key="index" class="vehicle">
                            <VehicleSelect
                                ref="outwardVehiclesRef"
                                :direction="Direction.Outward"
                                :vehicleIndex="index"
                                :vehicle="vehiclesOutward.length >= index ? vehiclesOutward[index - 1] : getEmptyVehicleItem()"
                            />
                        </div>
                    </div>
                    <div v-if="hasReturn && differentDetails" class="selections__vehicle">
                        <div class="title">Return</div>
                        <div class="quantity">
                            <h4>Add vehicles and bicycles</h4>
                            <VQuantity class="quantity-input" id="vehicleSelect" v-model="numberOfVehiclesReturn" :minValue="0" />
                        </div>
                        <div v-for="index in numberOfVehiclesReturn" :key="index" class="vehicle">
                            <VehicleSelect
                                ref="returnVehiclesRef"
                                :direction="Direction.Return"
                                :vehicleIndex="index"
                                :vehicle="vehiclesReturn.length >= index ? vehiclesReturn[index - 1] : getEmptyVehicleItem()"
                            />
                        </div>
                    </div>
                </div>
                <div v-if="hasReturn" class="checkbox-container">
                    <VCheckbox v-model="differentDetails" class="checkbox" id="return" />
                    <span>Different details for return</span>
                </div>
                <div class="vehicle-selection__button">
                    <VBtn>Add</VBtn>
                </div>
            </form>
        </div>
    </VModal>
</template>

<script setup lang="ts">
import { Direction } from "@/models/Enums";
import { getEmptyVehicleItem, useVehicleStore } from "@/store/vehicleStore";
import useVuelidate from "@vuelidate/core";
import { VBtn, VCheckbox, VModal, VQuantity } from "v-ferry-components";
import { computed, ref } from "vue";
import { VehicleItem } from "../../models/Vehicle";
import VehicleSelect from "./VehicleSelect.vue";

interface Props {
    hasReturn?: boolean;
}

defineProps<Props>();

const showVehiclesModal = ref(false);

const vehicleStore = useVehicleStore();

const vehiclesPlaceholder = computed(() => vehicleStore.getVehiclesTotal);

const numberOfVehiclesOutward = ref(vehicleStore.getVehiclesOfRoute(Direction.Outward).length);

const numberOfVehiclesReturn = ref(vehicleStore.getVehiclesOfRoute(Direction.Return).length);

const vehiclesOutward = computed(() => vehicleStore.getVehiclesOfRoute(Direction.Outward));
const vehiclesReturn = computed(() => vehicleStore.getVehiclesOfRoute(Direction.Return));

const differentDetails = computed({
    get(): boolean {
        return vehicleStore.getDifferentDetails;
    },
    set(value: boolean) {
        vehicleStore.setDifferentDetails(value);
    },
});

const v$ = useVuelidate();

const outwardVehiclesRef = ref();

const returnVehiclesRef = ref();

const submitVehicles = async () => {
    const isValid = await v$.value.$validate();

    if (!isValid) return;
    if (!outwardVehiclesRef.value) {
        showVehiclesModal.value = false;
        return;
    }

    const outwardVehicles = outwardVehiclesRef.value.map((v: { state: VehicleItem }) => v.state);

    vehicleStore.setVehicles(Direction.Outward, outwardVehicles);

    if (differentDetails.value && returnVehiclesRef.value) {
        const returnVehicles = returnVehiclesRef.value.map((v: { state: VehicleItem }) => v.state);
        vehicleStore.setVehicles(Direction.Return, returnVehicles);
    } else if (differentDetails.value) {
        vehicleStore.setVehicles(Direction.Return, []);
    }

    showVehiclesModal.value = false;
};
</script>

<style lang="scss" scoped>
.vehicle-select {
    .heading {
        color: var(--c-black);
        p {
            margin: 0;
            font-size: 16px;
        }
    }
    .vehicle-selection {
        .selections {
            @include flex(row, space-between, flex-start);

            @include tablet {
                flex-direction: column;
            }
            margin-top: 15px;
            gap: 20px;

            &__vehicle {
                margin-top: 10px;
                position: relative;
                flex: 1;

                @include tablet {
                    width: 100%;
                }

                .title {
                    text-align: end;
                    font-weight: bold;
                    font-size: 0.875rem;
                    position: absolute;
                    right: 5px;
                    top: -10px;
                }

                .quantity {
                    max-width: 360px;
                    @include flex(row, space-between, center, 10px);
                    margin-bottom: 5px;

                    @include tablet {
                        max-width: 100%;
                    }

                    h4 {
                        font-size: 1rem;
                        color: var(--c-black);
                    }

                    &-input {
                        max-width: 110px;
                        min-width: 100px;
                    }
                }

                .vehicle {
                    & + .vehicle {
                        margin-top: 30px;
                    }
                }

                & + .selections__vehicle {
                    @include tablet {
                        margin-top: 30px;
                    }
                }
            }
        }

        &__button {
            display: flex;
            justify-content: center;
            margin-top: 10px;

            button {
                width: 240px;
                padding: 15px 0;
                font-size: 1rem;
                font-weight: bold;
                border-radius: 4px;
                text-transform: uppercase;
            }
        }

        :deep(.vs__dropdown-toggle) {
            padding-top: 7px !important;
            padding-bottom: 7px !important;
            border-radius: 5px !important;
        }
    }

    .checkbox-container {
        margin: 10px 0;
        display: flex;
        color: var(--c-black);
    }
}

:deep(.v-modal) {
    h2 {
        font-size: 1.5rem;
        font-weight: normal;
    }
}
</style>
